/* eslint-disable array-callback-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Button,
  Container,
  Dimmer,
  Divider,
  Form,
  Grid,
  Header,
  List,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Popup,
  Segment,
  Table,
  Dropdown,
} from "semantic-ui-react";

import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

import { auth, db } from "../firebase/firebaseConfig";
import { useContext } from "react";
import { UserContext } from "../context/UserContext";
import useObtenerIngreso from "../hooks/useObtenerIngreso";

import GarantiaCabDB from "../firebase/GarantiaCabDB";
import GarantiaDB from "../firebase/GarantiaDB";
import GarantiaEjectDB from "../firebase/GarantiaEjectDB";
import GarantiaExternaDB from "../firebase/GarantiaExternaDB";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";

const toMillisSafe = (x) =>
  x?.toMillis
    ? x.toMillis()
    : x?.toDate
      ? x.toDate().getTime()
      : +new Date(x ?? 0);

const isEmpty = (v) => String(v ?? "").trim() === "";

const EjecutarGarantia = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { users } = useContext(UserContext);
  const user = auth.currentUser;

  const [ingreso] = useObtenerIngreso(id);

  // ============================================================
  // UI GLOBAL
  // ============================================================

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCliente, setShowCliente] = useState(true);

  // ============================================================
  // DATOS
  // ============================================================

  const [garantiaCab, setGarantiaCab] = useState([]);

  const [garantiaDiag, setGarantiaDiag] = useState([]);
  const [garantiaEjecAll, setGarantiaEjecAll] = useState([]);

  const [itemsDiagMaster, setItemsDiagMaster] = useState([]);
  const [itemsEjecMaster, setItemsEjecMaster] = useState([]);

  // Proveedores externos disponibles para garantía
  const [proveedores, setProveedores] = useState([]);

  // ============================================================
  // BUSCADORES
  // ============================================================

  const [qDiag, setQDiag] = useState("");
  const [showDiagResultados, setShowDiagResultados] =
    useState(false);
  const [diagVerTodos, setDiagVerTodos] = useState(false);

  const [qEjec, setQEjec] = useState("");
  const [showEjecResultados, setShowEjecResultados] =
    useState(false);
  const [ejecVerTodos, setEjecVerTodos] = useState(false);

  const [hoverId, setHoverId] = useState(null);

  // ============================================================
  // ETAPAS
  // ============================================================

  const [mostrarDiag, setMostrarDiag] = useState(false);
  const [mostrarEjec, setMostrarEjec] = useState(false);

  // ============================================================
  // MODALES
  // ============================================================

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteCol, setDeleteCol] = useState(null);

  const [openSiguiente, setOpenSiguiente] = useState(false);

  const [openFinalizar, setOpenFinalizar] =
    useState(false);

  const [
    observacionesFinales,
    setObservacionesFinales,
  ] = useState("");

  // ============================================================
  // VALIDACIÓN INICIAL DE GARANTÍA
  //
  // Esta etapa ocurre ANTES del diagnóstico.
  //
  // El técnico consulta el ERP y determina:
  //
  // LOCAL:
  // El equipo continúa con el flujo normal.
  //
  // EXTERNA:
  // El equipo fue comprado a un proveedor que debe
  // responder por la garantía.
  // ============================================================

  const [
    openValidacionGarantia,
    setOpenValidacionGarantia,
  ] = useState(false);

  const [tipoGarantia, setTipoGarantia] = useState("");

  const [
    proveedorGarantia,
    setProveedorGarantia,
  ] = useState("");

  // ============================================================
  // REFERENCIA CABECERA
  // ============================================================

  const cabIdRef = useRef("");

  // ============================================================
  // VOLVER
  // ============================================================

  const volver = useCallback(() => {
    navigate("/serviciotecnico/asignadostecnicos");
  }, [navigate]);

  // ============================================================
  // FECHA
  // ============================================================

  const formatearFecha = useCallback((fecha) => {
    if (!fecha?.toDate) return "";

    return moment(fecha.toDate()).format(
      "DD/MM/YYYY HH:mm"
    );
  }, []);

  // ============================================================
  // DATOS DEL INGRESO
  // ============================================================

  const datos = useMemo(() => {
    if (!ingreso) return null;

    return {
      folio: ingreso.folio ?? "",
      rut: ingreso.rut ?? "",
      entidad: ingreso.entidad ?? "",
      date: ingreso.date ?? null,
      telefono: ingreso.telefono ?? "",
      direccion: ingreso.direccion ?? "",
      correo: ingreso.correo ?? "",
      familia: ingreso.familia ?? "",
      tipo: ingreso.tipo ?? "",
      marca: ingreso.marca ?? "",
      modelo: ingreso.modelo ?? "",
      serie: ingreso.serie ?? "",
      servicio: ingreso.servicio ?? "",
    };
  }, [ingreso]);

  // ============================================================
  // FIRESTORE HELPERS
  // ============================================================

  const fetchDocs = useCallback(async (q) => {
    const snap = await getDocs(q);

    return snap.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    }));
  }, []);

  // ============================================================
  // CARGAR CABECERA GARANTÍA
  // ============================================================

  const loadCabecera = useCallback(async () => {
    if (!users?.emp_id || !id) return;

    const qCab = query(
      collection(db, "bitacoracabgarantia"),
      where("emp_id", "==", users.emp_id),
      where("id_cab_inst", "==", id)
    );

    const rows = await fetchDocs(qCab);

    setGarantiaCab(rows);

    if (rows.length) {
      cabIdRef.current = rows[0].id;
    } else {
      cabIdRef.current = "";
    }
  }, [users?.emp_id, id, fetchDocs]);

  // ============================================================
  // CARGAR DIAGNÓSTICO
  // ============================================================

  const loadDiagnostico = useCallback(async () => {
    if (!users?.emp_id || !cabIdRef.current) {
      setGarantiaDiag([]);
      return;
    }

    const qD = query(
      collection(db, "bitacoragarantia"),
      where("emp_id", "==", users.emp_id),
      where("id_cab_gar", "==", cabIdRef.current)
    );

    const rows = await fetchDocs(qD);

    setGarantiaDiag(rows);
  }, [users?.emp_id, fetchDocs]);

  // ============================================================
  // CARGAR EJECUCIÓN
  // ============================================================

  const loadEjecucion = useCallback(async () => {
    if (!users?.emp_id || !cabIdRef.current) {
      setGarantiaEjecAll([]);
      return;
    }

    const qE = query(
      collection(db, "bitacoragarantiaeject"),
      where("emp_id", "==", users.emp_id),
      where("id_cab_gar", "==", cabIdRef.current)
    );

    const rows = await fetchDocs(qE);

    setGarantiaEjecAll(rows);
  }, [users?.emp_id, fetchDocs]);

  // ============================================================
  // CARGAR ITEMS MAESTROS
  // ============================================================

  const loadItems = useCallback(async () => {
    if (!users?.emp_id) return;

    // Diagnóstico
    const qItemsSt = query(
      collection(db, "itemsst"),
      where("emp_id", "==", users.emp_id)
    );

    const rowsSt = await fetchDocs(qItemsSt);

    setItemsDiagMaster(rowsSt);

    // Ejecución
    const qItems = query(
      collection(db, "items"),
      where("emp_id", "==", users.emp_id)
    );

    const rows = await fetchDocs(qItems);

    setItemsEjecMaster(rows);
  }, [users?.emp_id, fetchDocs]);

  // ============================================================
  // CARGAR PROVEEDORES ST EXTERNOS
  // ============================================================

  const loadProveedores = useCallback(async () => {
    if (!users?.emp_id) {
      setProveedores([]);
      return;
    }

    try {
      const qProv = query(
        collection(db, "proveedores"),
        where("emp_id", "==", users.emp_id),
        where("categoria", "==", "ST_EXTERNO")
      );

      const rows = await fetchDocs(qProv);

      rows.sort((a, b) =>
        String(a.nombre ?? "").localeCompare(
          String(b.nombre ?? ""),
          "es"
        )
      );

      setProveedores(rows);
    } catch (error) {
      console.error(
        "Error cargando proveedores ST externos:",
        error
      );

      setProveedores([]);
    }
  }, [users?.emp_id, fetchDocs]);

  // ============================================================
  // REFRESCAR
  // ============================================================

  const refreshAll = useCallback(async () => {
    setLoading(true);

    try {
      await loadCabecera();

      await Promise.all([
        loadItems(),
        loadProveedores(),
      ]);

      if (cabIdRef.current) {
        await Promise.all([
          loadDiagnostico(),
          loadEjecucion(),
        ]);
      }
    } catch (e) {
      Swal.fire(
        "Error cargando garantía",
        String(e)
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadCabecera,
    loadItems,
    loadProveedores,
    loadDiagnostico,
    loadEjecucion,
  ]);

  useEffect(() => {
    if (!users?.emp_id || !id) return;

    refreshAll();
  }, [users?.emp_id, id, refreshAll]);

  // ============================================================
  // CABECERA ACTUAL
  // ============================================================

  const cab = useMemo(
    () =>
      garantiaCab.length
        ? garantiaCab[0]
        : null,
    [garantiaCab]
  );

  // ============================================================
  // OPCIONES PROVEEDORES ST EXTERNOS
  // ============================================================

  const proveedoresOptions = useMemo(() => {
    return proveedores.map((p) => ({
      key: p.id,
      value: p.id,
      text: p.nombre ?? "Proveedor sin nombre",
      description: p.rut ?? "",
    }));
  }, [proveedores]);

  const diagnosticoConfirmado =
    !!cab?.diagnostico;

  // ============================================================
  // DETERMINAR ETAPA
  // ============================================================

  useEffect(() => {
    if (!cab) return;

    /*
      NUEVO FLUJO

      1. validacion_garantia = false / inexistente
         -> todavía no sabemos si es LOCAL o EXTERNA.

      2. tipo_garantia = LOCAL
         -> mostrar diagnóstico.

      3. tipo_garantia = EXTERNA
         -> NO mostrar diagnóstico local.

      Compatibilidad:
      Las garantías antiguas que ya tenían diagnóstico
      y no poseen tipo_garantia se consideran LOCAL.
    */

    const esExterna =
      cab.tipo_garantia === "EXTERNA";

    const esLocal =
      cab.tipo_garantia === "LOCAL" ||
      (!cab.tipo_garantia &&
        !!cab.diagnostico);

    setMostrarDiag(
      esLocal && !esExterna
    );

    setMostrarEjec(
      esLocal &&
      !esExterna &&
      !!cab.diagnostico
    );
  }, [cab]);

  // ============================================================
  // ORDENAR DIAGNÓSTICO
  // ============================================================

  const diagOrdenado = useMemo(() => {
    return [...garantiaDiag].sort(
      (a, b) =>
        toMillisSafe(a?.fechaadd) -
        toMillisSafe(b?.fechaadd)
    );
  }, [garantiaDiag]);

  // ============================================================
  // ORDENAR EJECUCIÓN
  // ============================================================

  const ejecOrdenado = useMemo(() => {
    return [...garantiaEjecAll].sort(
      (a, b) =>
        toMillisSafe(a?.fechaadd) -
        toMillisSafe(b?.fechaadd)
    );
  }, [garantiaEjecAll]);

  // ============================================================
  // ITEMS DIAGNÓSTICO
  // ============================================================

  const diagItems = useMemo(() => {
    return diagOrdenado.filter((it) =>
      [
        "FALLA",
        "REPUESTO",
        "SERVICIO",
      ].includes(it.categoria)
    );
  }, [diagOrdenado]);

  const totalDiag = useMemo(
    () =>
      diagItems.reduce(
        (acc, it) =>
          acc + Number(it.price ?? 0),
        0
      ),
    [diagItems]
  );

  // ============================================================
  // ITEMS EJECUCIÓN
  // ============================================================

  const ejecInst = useMemo(
    () =>
      ejecOrdenado.filter(
        (it) =>
          it.categoria ===
          "INSTRUMENTOS"
      ),
    [ejecOrdenado]
  );
  const ejecCheck = useMemo(
    () =>
      ejecOrdenado.filter(
        (it) =>
          it.categoria === "CHECK"
      ),
    [ejecOrdenado]
  );

  const ejecMed = useMemo(
    () =>
      ejecOrdenado.filter(
        (it) =>
          it.categoria === "MEDICION"
      ),
    [ejecOrdenado]
  );

  const ejecSeg = useMemo(
    () =>
      ejecOrdenado.filter(
        (it) =>
          it.categoria === "SEGURIDAD"
      ),
    [ejecOrdenado]
  );

  // ============================================================
  // RESUMEN FINALIZACIÓN
  // ============================================================

  const resumenFinalizacion =
    useMemo(() => {
      return {
        fallas: diagItems.filter(
          (item) =>
            item.categoria === "FALLA"
        ).length,

        repuestos: diagItems.filter(
          (item) =>
            item.categoria ===
            "REPUESTO"
        ).length,

        servicios: diagItems.filter(
          (item) =>
            item.categoria ===
            "SERVICIO"
        ).length,

        instrumentos: ejecInst.length,
        checks: ejecCheck.length,
        mediciones: ejecMed.length,
        seguridad: ejecSeg.length,

        totalDiagnostico:
          diagItems.length,

        totalEjecucion:
          ejecOrdenado.length,
      };
    }, [
      diagItems,
      ejecInst,
      ejecCheck,
      ejecMed,
      ejecSeg,
      ejecOrdenado,
    ]);

  // ============================================================
  // MASTER DIAGNÓSTICO
  // ============================================================

  const diagMaster = useMemo(() => {
    return itemsDiagMaster
      .filter((it) =>
        [
          "FALLA",
          "REPUESTO",
          "SERVICIO",
        ].includes(it.categoria)
      )
      .sort(
        (a, b) =>
          toMillisSafe(a?.fechamod) -
          toMillisSafe(b?.fechamod)
      );
  }, [itemsDiagMaster]);

  // ============================================================
  // MASTER EJECUCIÓN
  // ============================================================

  const ejecMaster = useMemo(() => {
    return itemsEjecMaster
      .filter((it) =>
        [
          "CHECK",
          "MEDICION",
          "SEGURIDAD",
          "INSTRUMENTOS",
        ].includes(it.categoria)
      )
      .sort(
        (a, b) =>
          toMillisSafe(a?.fechamod) -
          toMillisSafe(b?.fechamod)
      );
  }, [itemsEjecMaster]);

  // ============================================================
  // EXCLUIR ITEMS YA AGREGADOS
  // ============================================================

  const diagBase = useMemo(() => {
    return diagMaster.filter(
      (it) =>
        !diagItems.some(
          (d) => d.item_id === it.id
        )
    );
  }, [diagMaster, diagItems]);

  const ejecBase = useMemo(() => {
    return ejecMaster.filter(
      (it) =>
        !ejecOrdenado.some(
          (d) => d.item_id === it.id
        )
    );
  }, [ejecMaster, ejecOrdenado]);

  // ============================================================
  // FILTRO DIAGNÓSTICO
  // ============================================================

  const diagResultados = useMemo(() => {
    if (diagVerTodos) return diagBase;

    const term = qDiag
      .trim()
      .toLowerCase();

    if (!term) return [];

    return diagBase.filter((it) => {
      const hay = `${it.nombre ?? ""} ${it.categoria ?? ""
        }`.toLowerCase();

      return hay.includes(term);
    });
  }, [
    diagVerTodos,
    qDiag,
    diagBase,
  ]);

  // ============================================================
  // FILTRO EJECUCIÓN
  // ============================================================

  const ejecResultados = useMemo(() => {
    if (ejecVerTodos) return ejecBase;

    const term = qEjec
      .trim()
      .toLowerCase();

    if (!term) return [];

    return ejecBase.filter((it) => {
      const hay = `${it.nombre ?? ""} ${it.categoria ?? ""
        }`.toLowerCase();

      return hay.includes(term);
    });
  }, [
    ejecVerTodos,
    qEjec,
    ejecBase,
  ]);

  // ============================================================
  // COMENZAR / CONTINUAR GARANTÍA
  // ============================================================

  const comenzarOContinuar =
    async () => {
      if (!users?.emp_id || !id)
        return;

      setSaving(true);

      try {
        const qCab = query(
          collection(
            db,
            "bitacoracabgarantia"
          ),
          where(
            "emp_id",
            "==",
            users.emp_id
          ),
          where(
            "id_cab_inst",
            "==",
            id
          )
        );

        const rows =
          await fetchDocs(qCab);

        // --------------------------------------------
        // YA EXISTE CABECERA
        // --------------------------------------------

        if (rows.length) {
          const actual = rows[0];

          cabIdRef.current =
            actual.id;

          setGarantiaCab(rows);

          // ------------------------------------------
          // GARANTÍA EXTERNA
          // ------------------------------------------

          if (
            actual.tipo_garantia ===
            "EXTERNA"
          ) {
            setMostrarDiag(false);
            setMostrarEjec(false);

            await Swal.fire({
              icon: "info",
              title:
                "Garantía externa",
              text: `Este equipo fue derivado a garantía externa${actual.proveedor_garantia
                ? ` con ${actual.proveedor_garantia}.`
                : "."
                }`,
            });

            return;
          }

          // ------------------------------------------
          // COMPATIBILIDAD CON REGISTROS ANTIGUOS
          // ------------------------------------------

          const esLocalLegacy =
            !actual.tipo_garantia &&
            !!actual.diagnostico;

          // ------------------------------------------
          // YA FUE VALIDADA COMO LOCAL
          // ------------------------------------------

          if (
            actual.validacion_garantia ===
            true ||
            actual.tipo_garantia ===
            "LOCAL" ||
            esLocalLegacy
          ) {
            setMostrarDiag(true);

            setMostrarEjec(
              !!actual.diagnostico
            );

            await loadDiagnostico();
            // Cerrar y limpiar el buscador de diagnóstico
            setShowDiagResultados(false);
            setDiagVerTodos(false);
            setQDiag("");

            await loadEjecucion();
            // Cerrar y limpiar el buscador de ejecución
            setShowEjecResultados(false);
            setEjecVerTodos(false);
            setQEjec("");

            Swal.fire({
              icon: "success",
              title:
                "Continuar garantía",
              timer: 900,
              showConfirmButton: false,
            });

            return;
          }

          // ------------------------------------------
          // EXISTE CABECERA PERO FALTA VALIDACIÓN ERP
          // ------------------------------------------

          setMostrarDiag(false);
          setMostrarEjec(false);

          setTipoGarantia("");
          setProveedorGarantia("");

          setOpenValidacionGarantia(
            true
          );

          return;
        }

        // --------------------------------------------
        // NUEVA GARANTÍA
        // --------------------------------------------

        const now = new Date();

        await GarantiaCabDB({
          id_cab_inst: id,

          folio:
            datos?.folio ?? "",

          confirmado: false,

          // Todavía no sabemos si será
          // local o externa.
          estado:
            "VALIDANDO_GARANTIA",

          diagnostico: false,

          cerrado: false,

          fecha_cerrado: "",

          userAdd:
            user?.email ?? "",

          userMod:
            user?.email ?? "",

          fechaAdd: now,
          fechaMod: now,

          emp_id: users.emp_id,
        });

        // Recuperar ID de la cabecera creada.
        await loadCabecera();

        setMostrarDiag(false);
        setMostrarEjec(false);

        setTipoGarantia("");
        setProveedorGarantia("");

        // Mostrar evaluación ERP.
        setOpenValidacionGarantia(
          true
        );
      } catch (e) {
        Swal.fire(
          "Error iniciando garantía",
          String(e)
        );
      } finally {
        setSaving(false);
      }
    };

  // ============================================================
  // CONFIRMAR VALIDACIÓN INICIAL
  // ============================================================

  const confirmarValidacionGarantia = async () => {
    if (!cabIdRef.current) {
      return Swal.fire({
        icon: "warning",
        title: "Cabecera no encontrada",
        text: "No se encontró la cabecera de la garantía.",
      });
    }

    if (!tipoGarantia) {
      return Swal.fire({
        icon: "warning",
        title: "Selecciona una opción",
        text:
          "Indica si corresponde a garantía local o garantía de proveedor externo.",
      });
    }

    if (
      tipoGarantia === "EXTERNA" &&
      !proveedorGarantia
    ) {
      return Swal.fire({
        icon: "warning",
        title: "Proveedor requerido",
        text:
          "Selecciona el proveedor identificado en el ERP.",
      });
    }

    const proveedorSeleccionado =
      tipoGarantia === "EXTERNA"
        ? proveedores.find(
          (p) => p.id === proveedorGarantia
        )
        : null;

    if (
      tipoGarantia === "EXTERNA" &&
      !proveedorSeleccionado
    ) {
      return Swal.fire({
        icon: "warning",
        title: "Proveedor no encontrado",
        text:
          "No fue posible encontrar el proveedor seleccionado.",
      });
    }

    setSaving(true);

    try {
      const now = new Date();

      const batch = writeBatch(db);

      // ========================================================
      // GARANTÍA LOCAL
      // ========================================================

      if (tipoGarantia === "LOCAL") {
        batch.update(
          doc(
            db,
            "bitacoracabgarantia",
            cabIdRef.current
          ),
          {
            validacion_garantia: true,

            tipo_garantia: "LOCAL",

            derivado_externo: false,

            proveedor_id: "",
            proveedor_garantia: "",
            proveedor_rut: "",
            proveedor_contacto: "",
            proveedor_correo: "",
            proveedor_telefono: "",

            estado: "EJECUTANDO",

            usermod: user?.email ?? "",
            fechamod: now,
          }
        );
      }

      // ========================================================
      // GARANTÍA EXTERNA
      // ========================================================

      if (tipoGarantia === "EXTERNA") {

        // ========================================================
        // CREAR CASO DE GARANTÍA EXTERNA
        // ========================================================

        const idGarantiaExterna =
          await GarantiaExternaDB({
            empId: users.emp_id,
            idIngreso: id,
            idGarantia: cabIdRef.current,
            ingreso: datos,
            proveedor: proveedorSeleccionado,
            usuario: user?.email ?? "",
          });

        // ========================================================
        // ACTUALIZAR CABECERA DE GARANTÍA
        // ========================================================

        batch.update(
          doc(
            db,
            "bitacoracabgarantia",
            cabIdRef.current
          ),
          {
            validacion_garantia: true,

            tipo_garantia: "EXTERNA",

            derivado_externo: true,

            // Relación directa con el nuevo caso
            id_garantia_externa:
              idGarantiaExterna,

            proveedor_id:
              proveedorSeleccionado.id,

            proveedor_garantia:
              proveedorSeleccionado.nombre ?? "",

            proveedor_rut:
              proveedorSeleccionado.rut ?? "",

            proveedor_contacto:
              proveedorSeleccionado.contacto ?? "",

            proveedor_correo:
              proveedorSeleccionado.correo ?? "",

            proveedor_telefono:
              proveedorSeleccionado.telefono ?? "",

            estado: "DERIVADO_EXTERNO",

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        // ========================================================
        // ACTUALIZAR ORDEN DE INGRESO ST
        // ========================================================

        batch.update(
          doc(
            db,
            "ingresostcab",
            id
          ),
          {
            estado_servicio:
              "GARANTIA_EXTERNA",

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );
      }

      await batch.commit();

      await loadCabecera();

      setOpenValidacionGarantia(false);

      // ========================================================
      // CONTINUAR FLUJO LOCAL
      // ========================================================

      if (tipoGarantia === "LOCAL") {
        setMostrarDiag(true);
        setMostrarEjec(false);

        await Swal.fire({
          icon: "success",
          title: "Garantía local",
          text:
            "El equipo continuará con el diagnóstico y flujo de garantía actual.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ========================================================
      // GARANTÍA EXTERNA
      // ========================================================

      if (tipoGarantia === "EXTERNA") {
        setMostrarDiag(false);
        setMostrarEjec(false);

        await Swal.fire({
          icon: "success",
          title: "Garantía externa",
          text: `El equipo fue derivado a ${proveedorSeleccionado.nombre}.`,
          timer: 1700,
          showConfirmButton: false,
        });
      }

      setTipoGarantia("");
      setProveedorGarantia("");
    } catch (error) {
      console.error(
        "Error validando garantía:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Error validando garantía",
        text:
          error?.message ||
          "No fue posible guardar la validación.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // AGREGAR ITEM DIAGNÓSTICO
  // ============================================================

  const agregarDiagItem =
    async (itemId) => {
      if (!cabIdRef.current)
        return Swal.fire(
          "Primero comienza la garantía."
        );

      setSaving(true);

      try {
        const it =
          diagMaster.find(
            (x) => x.id === itemId
          );

        if (!it) return;

        if (
          diagItems.some(
            (d) =>
              d.item_id === it.id
          )
        ) {
          Swal.fire(
            "Item ya está agregado."
          );

          return;
        }

        const now = new Date();

        await GarantiaDB({
          folio:
            datos?.folio ?? "",

          id_cab_gar:
            cabIdRef.current,

          id_cab_inst: id,

          item_id: it.id,

          item: it.nombre,

          categoria:
            it.categoria,

          price: it.price
            ? parseFloat(it.price)
            : 0,

          userAdd:
            user?.email ?? "",

          userMod:
            user?.email ?? "",

          fechaAdd: now,
          fechaMod: now,

          emp_id:
            users.emp_id,
        });

        await loadDiagnostico();
      } catch (e) {
        Swal.fire(
          "Error agregando item",
          String(e)
        );
      } finally {
        setSaving(false);
      }
    };

  // ============================================================
  // AGREGAR ITEM EJECUCIÓN
  // ============================================================

  const agregarEjecItem =
    async (itemId) => {
      if (!cabIdRef.current)
        return Swal.fire(
          "Primero completa el diagnóstico."
        );

      setSaving(true);

      try {
        const it =
          ejecMaster.find(
            (x) => x.id === itemId
          );

        if (!it) return;

        const ya =
          ejecOrdenado.some(
            (d) =>
              d.item_id === it.id
          );

        if (ya) {
          Swal.fire(
            "Item ya está agregado."
          );

          return;
        }

        const now = new Date();

        await GarantiaEjectDB({
          folio:
            datos?.folio ?? "",

          id_cab_gar:
            cabIdRef.current,

          id_cab_inst: id,

          item_id: it.id,

          item: it.nombre,

          categoria:
            it.categoria,

          medida: it.medida,

          inicial:
            it.inicial !== undefined
              ? parseFloat(it.inicial)
              : 0,

          final:
            it.final !== undefined
              ? parseFloat(it.final)
              : 0,

          valor: "",

          userAdd:
            user?.email ?? "",

          userMod:
            user?.email ?? "",

          fechaAdd: now,
          fechaMod: now,

          emp_id:
            users.emp_id,
        });

        await loadEjecucion();
      } catch (e) {
        Swal.fire(
          "Error agregando item",
          String(e)
        );
      } finally {
        setSaving(false);
      }
    };

  // ============================================================
  // ELIMINAR
  // ============================================================

  const askDelete = (
    docId,
    col
  ) => {
    setDeleteId(docId);
    setDeleteCol(col);
    setOpenDelete(true);
  };

  const doDelete = async () => {
    if (
      !deleteId ||
      !deleteCol
    )
      return;

    setOpenDelete(false);
    setSaving(true);

    try {
      if (
        deleteCol === "diag"
      ) {
        await deleteDoc(
          doc(
            db,
            "bitacoragarantia",
            deleteId
          )
        );

        await loadDiagnostico();
      } else {
        await deleteDoc(
          doc(
            db,
            "bitacoragarantiaeject",
            deleteId
          )
        );

        await loadEjecucion();
      }
    } catch (e) {
      Swal.fire(
        "Error eliminando",
        String(e)
      );
    } finally {
      setSaving(false);
      setDeleteId("");
      setDeleteCol(null);
    }
  };

  // ============================================================
  // VALIDAR SIGUIENTE
  // ============================================================

  const canSiguiente =
    useMemo(() => {
      const existeAny =
        diagItems.length > 0;

      const existeF =
        diagItems.some(
          (d) =>
            d.categoria === "FALLA"
        );

      const existeS =
        diagItems.some(
          (d) =>
            d.categoria ===
            "SERVICIO"
        );

      if (!existeAny)
        return {
          ok: false,
          msg:
            "Agrega al menos 1 item al diagnóstico.",
        };

      if (!existeF)
        return {
          ok: false,
          msg:
            "Falta al menos 1 item de FALLA.",
        };

      if (!existeS)
        return {
          ok: false,
          msg:
            "Falta al menos 1 item de SERVICIO.",
        };

      return {
        ok: true,
        msg: "",
      };
    }, [diagItems]);

  // ============================================================
  // CONFIRMAR DIAGNÓSTICO
  //
  // IMPORTANTE:
  // Aquí YA NO preguntamos LOCAL / EXTERNA.
  // Esa decisión ocurrió antes del diagnóstico.
  // ============================================================

  const confirmarSiguiente =
    async () => {
      const v = canSiguiente;

      if (!v.ok)
        return Swal.fire(v.msg);

      setOpenSiguiente(false);
      setSaving(true);

      try {
        const now = new Date();

        const batch =
          writeBatch(db);

        batch.update(
          doc(
            db,
            "bitacoracabgarantia",
            cabIdRef.current
          ),
          {
            diagnostico: true,

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        batch.update(
          doc(
            db,
            "ingresostcab",
            id
          ),
          {
            estado_servicio:
              "DIAGNOSTICADO",

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        await batch.commit();

        await loadCabecera();

        setMostrarEjec(true);

        Swal.fire({
          icon: "success",

          title:
            "Diagnóstico confirmado",

          timer: 900,

          showConfirmButton:
            false,
        });
      } catch (e) {
        Swal.fire(
          "Error confirmando diagnóstico",
          String(e)
        );
      } finally {
        setSaving(false);
      }
    };

  // ============================================================
  // ACTUALIZAR VALORES EN MEMORIA
  // ============================================================

  const updateMedValor = (
    idDoc,
    valor
  ) => {
    setGarantiaEjecAll(
      (prev) =>
        prev.map((x) =>
          x.id === idDoc
            ? {
              ...x,
              valor,
            }
            : x
        )
    );
  };

  const updateSegValor = (
    idDoc,
    valor
  ) => {
    setGarantiaEjecAll(
      (prev) =>
        prev.map((x) =>
          x.id === idDoc
            ? {
              ...x,
              valor,
            }
            : x
        )
    );
  };

  // ============================================================
  // VALIDAR FINALIZACIÓN
  // ============================================================

  const canFinalizar =
    useMemo(() => {
      const existeEjec =
        ejecOrdenado.length > 0;

      if (!existeEjec)
        return {
          ok: false,
          msg:
            "Agrega al menos 1 item de ejecución.",
        };

      const medEmpty =
        ejecMed.filter((x) =>
          isEmpty(x.valor)
        );

      const segEmpty =
        ejecSeg.filter((x) =>
          isEmpty(x.valor)
        );

      if (medEmpty.length)
        return {
          ok: false,
          msg:
            "Hay items de MEDICIÓN sin valor.",
        };

      if (segEmpty.length)
        return {
          ok: false,
          msg:
            "Hay items de SEGURIDAD sin valor.",
        };

      return {
        ok: true,
        msg: "",
      };
    }, [
      ejecOrdenado,
      ejecMed,
      ejecSeg,
    ]);

  // ============================================================
  // GUARDAR VALORES EJECUCIÓN
  // ============================================================

  const guardarValoresEjec =
    useCallback(async () => {
      const now = new Date();

      const batch =
        writeBatch(db);

      const ref = collection(
        db,
        "bitacoragarantiaeject"
      );

      ejecMed.forEach((d) => {
        batch.update(
          doc(ref, d.id),
          {
            valor: d.valor,

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );
      });

      ejecSeg.forEach((d) => {
        batch.update(
          doc(ref, d.id),
          {
            valor: d.valor,

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );
      });

      await batch.commit();
    }, [
      ejecMed,
      ejecSeg,
      user?.email,
    ]);

  // ============================================================
  // FINALIZAR GARANTÍA LOCAL
  // ============================================================

  const confirmarFinalizar =
    async () => {
      const validacion =
        canFinalizar;

      if (!validacion.ok) {
        return Swal.fire({
          icon: "warning",

          title:
            "No se puede finalizar",

          text:
            validacion.msg,
        });
      }

      if (!cabIdRef.current) {
        return Swal.fire({
          icon: "warning",

          title:
            "Cabecera no encontrada",

          text:
            "No se encontró la cabecera de la garantía.",
        });
      }

      setSaving(true);

      try {
        // Guardar valores de medición
        // y seguridad.

        await guardarValoresEjec();

        const now = new Date();

        const observaciones =
          observacionesFinales.trim();

        const batch =
          writeBatch(db);

        // --------------------------------------------
        // INGRESO
        // --------------------------------------------

        batch.update(
          doc(
            db,
            "ingresostcab",
            id
          ),
          {
            enproceso: "0",

            estado:
              "REALIZADO",

            estado_servicio:
              "REALIZADO",

            fecha_out: now,

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        // --------------------------------------------
        // CABECERA GARANTÍA
        // --------------------------------------------

        batch.update(
          doc(
            db,
            "bitacoracabgarantia",
            cabIdRef.current
          ),
          {
            confirmado: true,

            cerrado: true,

            estado:
              "REALIZADO",

            fecha_cerrado: now,

            observacionesFinales:
              observaciones,

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        await batch.commit();

        setOpenFinalizar(false);

        setObservacionesFinales(
          ""
        );

        await Swal.fire({
          icon: "success",

          title:
            "Garantía finalizada",

          text:
            "La ejecución fue finalizada correctamente.",

          timer: 1400,

          showConfirmButton:
            false,
        });

        volver();
      } catch (error) {
        console.error(
          "Error finalizando garantía:",
          error
        );

        Swal.fire({
          icon: "error",

          title:
            "Error finalizando garantía",

          text:
            error?.message ||
            "No fue posible finalizar la garantía.",
        });
      } finally {
        setSaving(false);
      }
    };

  // ============================================================
  // GUARD: ESPERAR DATOS DEL INGRESO
  // ============================================================

  if (!datos) {
    return (
      <Container>
        <Segment>
          <Message info icon>
            <Icon name="info circle" />

            <Message.Content>
              <Message.Header>
                Cargando ingreso...
              </Message.Header>
            </Message.Content>
          </Message>
        </Segment>
      </Container>
    );
  }


  return (
    <Container fluid style={{ paddingTop: 20, paddingBottom: 40 }}>
      <Dimmer active={loading || saving} inverted>
        <Loader>
          {saving ? "Guardando..." : "Cargando..."}
        </Loader>
      </Dimmer>

      {/* =======================================================
          CABECERA
      ======================================================= */}

      <Segment>
        <Grid verticalAlign="middle">
          <Grid.Row>
            <Grid.Column width={12}>
              <Header as="h2">
                <Icon name="shield alternate" />
                <Header.Content>
                  Garantía
                  <Header.Subheader>
                    Orden de ingreso N° {datos?.folio || "-"}
                  </Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>

            <Grid.Column width={4} textAlign="right">
              <Button
                icon
                labelPosition="left"
                onClick={volver}
                disabled={saving}
              >
                <Icon name="arrow left" />
                Volver
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      {/* =======================================================
          DATOS CLIENTE / EQUIPO
      ======================================================= */}

      <Segment>
        <Header
          as="h4"
          dividing
          style={{ cursor: "pointer" }}
          onClick={() => setShowCliente((prev) => !prev)}
        >
          <Icon name={showCliente ? "angle down" : "angle right"} />
          Datos del ingreso
        </Header>

        {showCliente && (
          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column>
                <List relaxed>
                  <List.Item>
                    <List.Icon name="user" />
                    <List.Content>
                      <List.Header>Cliente</List.Header>
                      <List.Description>
                        {datos?.entidad || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>

                  <List.Item>
                    <List.Icon name="id card" />
                    <List.Content>
                      <List.Header>RUT</List.Header>
                      <List.Description>
                        {datos?.rut || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>

                  <List.Item>
                    <List.Icon name="phone" />
                    <List.Content>
                      <List.Header>Teléfono</List.Header>
                      <List.Description>
                        {datos?.telefono || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>

                  <List.Item>
                    <List.Icon name="mail" />
                    <List.Content>
                      <List.Header>Correo</List.Header>
                      <List.Description>
                        {datos?.correo || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>

              <Grid.Column>
                <List relaxed>
                  <List.Item>
                    <List.Icon name="cogs" />
                    <List.Content>
                      <List.Header>Equipo</List.Header>
                      <List.Description>
                        {datos?.familia || "-"} / {datos?.tipo || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>

                  <List.Item>
                    <List.Icon name="tag" />
                    <List.Content>
                      <List.Header>Marca / Modelo</List.Header>
                      <List.Description>
                        {datos?.marca || "-"} / {datos?.modelo || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>

                  <List.Item>
                    <List.Icon name="barcode" />
                    <List.Content>
                      <List.Header>Serie</List.Header>
                      <List.Description>
                        {datos?.serie || "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>

                  <List.Item>
                    <List.Icon name="calendar alternate outline" />
                    <List.Content>
                      <List.Header>Fecha ingreso</List.Header>
                      <List.Description>
                        {datos?.date ? formatearFecha(datos.date) : "-"}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Segment>

      {/* =======================================================
          ESTADO DE GARANTÍA
      ======================================================= */}

      {cab?.tipo_garantia === "EXTERNA" && (
        <Message warning icon>
          <Icon name="truck" />

          <Message.Content>
            <Message.Header>
              Garantía externa
            </Message.Header>

            Este equipo fue identificado como garantía de proveedor externo.

            {cab?.proveedor_garantia && (
              <>
                <br />
                <strong>
                  Proveedor:
                </strong>{" "}
                {cab.proveedor_garantia}
              </>
            )}
          </Message.Content>
        </Message>
      )}

      {cab?.tipo_garantia === "LOCAL" && (
        <Message positive icon>
          <Icon name="wrench" />

          <Message.Content>
            <Message.Header>
              Garantía local
            </Message.Header>

            El equipo continuará con diagnóstico y reparación en Servicio Técnico.
          </Message.Content>
        </Message>
      )}

      {/* =======================================================
          COMENZAR GARANTÍA
      ======================================================= */}

      {!cab && (
        <Segment textAlign="center">
          <Header as="h4">
            La garantía aún no ha sido iniciada.
          </Header>

          <Button
            primary
            icon
            labelPosition="left"
            onClick={comenzarOContinuar}
            loading={saving}
            disabled={saving}
          >
            <Icon name="play" />
            Comenzar Garantía
          </Button>
        </Segment>
      )}

      {/* =======================================================
          CABECERA EXISTE PERO NO ESTÁ VALIDADA
      ======================================================= */}

      {cab &&
        !cab.validacion_garantia &&
        !cab.tipo_garantia &&
        !cab.diagnostico && (
          <Segment textAlign="center">
            <Message info>
              <Message.Header>
                Validación de garantía pendiente
              </Message.Header>

              Antes de iniciar el diagnóstico debes verificar en el ERP
              si este equipo corresponde a garantía de proveedor externo.
            </Message>

            <Button
              primary
              icon
              labelPosition="left"
              onClick={comenzarOContinuar}
              disabled={saving}
              loading={saving}
            >
              <Icon name="search" />
              Validar Garantía
            </Button>
          </Segment>
        )}

      {/* =======================================================
          DIAGNÓSTICO
      ======================================================= */}

      {mostrarDiag && (
        <Segment>
          <Header as="h3" dividing>
            <Icon name="search" />
            <Header.Content>
              Diagnóstico
              <Header.Subheader>
                Identifica fallas, repuestos y servicios requeridos.
              </Header.Subheader>
            </Header.Content>
          </Header>

          {!diagnosticoConfirmado && (
            <>
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={13}>
                    <Input
                      fluid
                      icon="search"
                      iconPosition="left"
                      placeholder="Buscar FALLA, REPUESTO o SERVICIO..."
                      value={qDiag}
                      onChange={(e) => {
                        setQDiag(e.target.value);
                        setDiagVerTodos(false);
                        setShowDiagResultados(true);
                      }}
                      onFocus={() => {
                        if (qDiag.trim()) {
                          setShowDiagResultados(true);
                        }
                      }}
                    />
                  </Grid.Column>

                  <Grid.Column width={3}>
                    <Button
                      fluid
                      basic
                      icon
                      labelPosition="left"
                      onClick={() => {
                        setDiagVerTodos((prev) => !prev);
                        setShowDiagResultados(true);
                      }}
                    >
                      <Icon name="list" />
                      {diagVerTodos ? "Ocultar" : "Ver todos"}
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              {showDiagResultados && diagResultados.length > 0 && (
                <Segment
                  style={{
                    maxHeight: 280,
                    overflowY: "auto",
                    marginTop: 10,
                  }}
                >
                  <List divided relaxed selection>
                    {diagResultados.map((item) => (
                      <List.Item
                        key={item.id}
                        onMouseEnter={() => setHoverId(item.id)}
                        onMouseLeave={() => setHoverId(null)}
                      >
                        <List.Content floated="right">
                          <Button
                            size="mini"
                            positive
                            icon
                            labelPosition="left"
                            onClick={(e) => {
                              e.currentTarget.blur();
                              setShowDiagResultados(false);
                              setDiagVerTodos(false);
                              setQDiag("");
                              agregarDiagItem(item.id);
                            }}
                            loading={saving}
                            disabled={saving}
                          >
                            <Icon name="plus" />
                            Agregar
                          </Button>
                        </List.Content>

                        <List.Icon
                          name={
                            item.categoria === "FALLA"
                              ? "warning sign"
                              : item.categoria === "REPUESTO"
                                ? "cog"
                                : "wrench"
                          }
                          size="large"
                          verticalAlign="middle"
                        />

                        <List.Content>
                          <List.Header>
                            {item.nombre}
                          </List.Header>

                          <List.Description>
                            {item.categoria}
                            {hoverId === item.id && item.price
                              ? ` · $${Number(item.price).toLocaleString(
                                "es-CL"
                              )}`
                              : ""}
                          </List.Description>
                        </List.Content>
                      </List.Item>
                    ))}
                  </List>
                </Segment>
              )}
            </>
          )}

          <Divider />

          <Table celled striped compact="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>
                  Categoría
                </Table.HeaderCell>

                <Table.HeaderCell>
                  Item
                </Table.HeaderCell>

                <Table.HeaderCell width={3} textAlign="right">
                  Valor
                </Table.HeaderCell>

                {!diagnosticoConfirmado && (
                  <Table.HeaderCell width={1} textAlign="center">
                    Acción
                  </Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {diagItems.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={diagnosticoConfirmado ? 3 : 4}>
                    <Message info>
                      Aún no hay items agregados al diagnóstico.
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}

              {diagItems.map((item) => (
                <Table.Row key={item.id}>
                  <Table.Cell>
                    {item.categoria}
                  </Table.Cell>

                  <Table.Cell>
                    {item.item}
                  </Table.Cell>

                  <Table.Cell textAlign="right">
                    $
                    {Number(item.price || 0).toLocaleString("es-CL")}
                  </Table.Cell>

                  {!diagnosticoConfirmado && (
                    <Table.Cell textAlign="center">
                      <Popup
                        content="Eliminar"
                        trigger={
                          <Button
                            icon="trash"
                            size="mini"
                            negative
                            basic
                            onClick={() => askDelete(item.id, "diag")}
                          />
                        }
                      />
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={2}>
                  Total
                </Table.HeaderCell>

                <Table.HeaderCell textAlign="right">
                  ${Number(totalDiag).toLocaleString("es-CL")}
                </Table.HeaderCell>

                {!diagnosticoConfirmado && <Table.HeaderCell />}
              </Table.Row>
            </Table.Footer>
          </Table>

          {!diagnosticoConfirmado && (
            <div style={{ textAlign: "right", marginTop: 20 }}>
              <Button
                primary
                icon
                labelPosition="right"
                onClick={() => {
                  const v = canSiguiente;

                  if (!v.ok) {
                    Swal.fire({
                      icon: "warning",
                      title: "Diagnóstico incompleto",
                      text: v.msg,
                    });

                    return;
                  }

                  setOpenSiguiente(true);
                }}
              >
                Siguiente
                <Icon name="arrow right" />
              </Button>
            </div>
          )}
        </Segment>
      )}

      {/* =======================================================
          EJECUCIÓN
      ======================================================= */}

      {mostrarEjec && (
        <Segment>
          <Header as="h3" dividing>
            <Icon name="wrench" />
            <Header.Content>
              Ejecución
              <Header.Subheader>
                Registra las actividades realizadas durante la reparación.
              </Header.Subheader>
            </Header.Content>
          </Header>

          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={13}>
                <Input
                  fluid
                  icon="search"
                  iconPosition="left"
                  placeholder="Buscar CHECK, MEDICIÓN, SEGURIDAD o INSTRUMENTOS..."
                  value={qEjec}
                  onChange={(e) => {
                    setQEjec(e.target.value);
                    setEjecVerTodos(false);
                    setShowEjecResultados(true);
                  }}
                  onFocus={() => {
                    if (qEjec.trim()) {
                      setShowEjecResultados(true);
                    }
                  }}
                />
              </Grid.Column>

              <Grid.Column width={3}>
                <Button
                  fluid
                  basic
                  icon
                  labelPosition="left"
                  onClick={() => {
                    setEjecVerTodos((prev) => !prev);
                    setShowEjecResultados(true);
                  }}
                >
                  <Icon name="list" />
                  {ejecVerTodos ? "Ocultar" : "Ver todos"}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>

          {showEjecResultados && ejecResultados.length > 0 && (
            <Segment
              style={{
                maxHeight: 280,
                overflowY: "auto",
                marginTop: 10,
              }}
            >
              <List divided relaxed selection>
                {ejecResultados.map((item) => (
                  <List.Item key={item.id}>
                    <List.Content floated="right">
                      <Button
                        size="mini"
                        positive
                        icon
                        labelPosition="left"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          setShowEjecResultados(false);
                          setEjecVerTodos(false);
                          setQEjec("");
                          agregarEjecItem(item.id);
                        }}
                      >
                        <Icon name="plus" />
                        Agregar
                      </Button>
                    </List.Content>

                    <List.Icon
                      name={
                        item.categoria === "CHECK"
                          ? "check circle"
                          : item.categoria === "MEDICION"
                            ? "dashboard"
                            : item.categoria === "SEGURIDAD"
                              ? "shield"
                              : "lab"
                      }
                      size="large"
                      verticalAlign="middle"
                    />

                    <List.Content>
                      <List.Header>
                        {item.nombre}
                      </List.Header>

                      <List.Description>
                        {item.categoria}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            </Segment>
          )}

          <Divider />

          {/* ===================================================
              INSTRUMENTOS
          =================================================== */}

          {ejecInst.length > 0 && (
            <>
              <Header as="h4">
                <Icon name="lab" />
                Instrumentos
              </Header>

              <Table celled compact striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Item</Table.HeaderCell>
                    <Table.HeaderCell width={2}>
                      Acción
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {ejecInst.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell>{item.item}</Table.Cell>

                      <Table.Cell textAlign="center">
                        <Button
                          icon="trash"
                          size="mini"
                          negative
                          basic
                          onClick={() => askDelete(item.id, "ejec")}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}

          {/* ===================================================
              CHECK
          =================================================== */}

          {ejecCheck.length > 0 && (
            <>
              <Header as="h4">
                <Icon name="check circle" />
                Check
              </Header>

              <Table celled compact striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Item</Table.HeaderCell>
                    <Table.HeaderCell width={2}>
                      Acción
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {ejecCheck.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell>{item.item}</Table.Cell>

                      <Table.Cell textAlign="center">
                        <Button
                          icon="trash"
                          size="mini"
                          negative
                          basic
                          onClick={() => askDelete(item.id, "ejec")}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}

          {/* ===================================================
              MEDICIÓN
          =================================================== */}

          {ejecMed.length > 0 && (
            <>
              <Header as="h4">
                <Icon name="dashboard" />
                Medición
              </Header>

              <Table celled compact striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Item</Table.HeaderCell>
                    <Table.HeaderCell width={2}>
                      Inicial
                    </Table.HeaderCell>
                    <Table.HeaderCell width={2}>
                      Final
                    </Table.HeaderCell>
                    <Table.HeaderCell width={3}>
                      Valor
                    </Table.HeaderCell>
                    <Table.HeaderCell width={2}>
                      Acción
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {ejecMed.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell>
                        {item.item}
                      </Table.Cell>

                      <Table.Cell>
                        {item.inicial ?? ""}
                      </Table.Cell>

                      <Table.Cell>
                        {item.final ?? ""}
                      </Table.Cell>

                      <Table.Cell>
                        <Input
                          fluid
                          value={item.valor ?? ""}
                          onChange={(e) =>
                            updateMedValor(item.id, e.target.value)
                          }
                        />
                      </Table.Cell>

                      <Table.Cell textAlign="center">
                        <Button
                          icon="trash"
                          size="mini"
                          negative
                          basic
                          onClick={() => askDelete(item.id, "ejec")}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}

          {/* ===================================================
              SEGURIDAD
          =================================================== */}

          {ejecSeg.length > 0 && (
            <>
              <Header as="h4">
                <Icon name="shield" />
                Seguridad
              </Header>

              <Table celled compact striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Item</Table.HeaderCell>
                    <Table.HeaderCell width={4}>
                      Valor
                    </Table.HeaderCell>
                    <Table.HeaderCell width={2}>
                      Acción
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {ejecSeg.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell>
                        {item.item}
                      </Table.Cell>

                      <Table.Cell>
                        <Input
                          fluid
                          value={item.valor ?? ""}
                          onChange={(e) =>
                            updateSegValor(item.id, e.target.value)
                          }
                        />
                      </Table.Cell>

                      <Table.Cell textAlign="center">
                        <Button
                          icon="trash"
                          size="mini"
                          negative
                          basic
                          onClick={() => askDelete(item.id, "ejec")}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}

          <Divider />

          <div style={{ textAlign: "right" }}>
            <Button
              positive
              icon
              labelPosition="left"
              onClick={() => {
                const v = canFinalizar;

                if (!v.ok) {
                  Swal.fire({
                    icon: "warning",
                    title: "Ejecución incompleta",
                    text: v.msg,
                  });

                  return;
                }

                setOpenFinalizar(true);
              }}
            >
              <Icon name="check" />
              Finalizar Garantía
            </Button>
          </div>
        </Segment>
      )}

      {/* =======================================================
          MODAL VALIDACIÓN INICIAL DE GARANTÍA
      ======================================================= */}

      <Modal
        open={openValidacionGarantia}
        size="small"
        closeOnDimmerClick={!saving}
        closeOnEscape={!saving}
        onClose={() => {
          if (saving) return;

          setOpenValidacionGarantia(false);
          setTipoGarantia("");
          setProveedorGarantia("");
        }}
      >
        <Modal.Header>
          Validación inicial de garantía
        </Modal.Header>

        <Modal.Content>
          <Message info icon>
            <Icon name="database" />

            <Message.Content>
              <Message.Header>
                Verificación en ERP
              </Message.Header>

              Antes de realizar el diagnóstico, verifica por la serie del
              equipo si fue adquirido a un proveedor que debe responder por
              la garantía.
            </Message.Content>
          </Message>

          <Segment secondary>
            <Grid stackable columns={2}>
              <Grid.Column>
                <strong>Equipo</strong>
                <div>
                  {datos?.marca || "-"} {datos?.modelo || ""}
                </div>
              </Grid.Column>

              <Grid.Column>
                <strong>Serie</strong>
                <div>{datos?.serie || "-"}</div>
              </Grid.Column>
            </Grid>
          </Segment>

          <Header as="h4">
            ¿Quién responde por esta garantía?
          </Header>

          <Grid columns={2} stackable>
            <Grid.Column>
              <Segment
                raised={tipoGarantia === "LOCAL"}
                color={
                  tipoGarantia === "LOCAL"
                    ? "teal"
                    : undefined
                }
                onClick={() => {
                  if (saving) return;

                  setTipoGarantia("LOCAL");
                  setProveedorGarantia("");
                }}
                style={{
                  cursor: saving ? "default" : "pointer",
                  minHeight: 155,
                }}
              >
                <Header as="h4" textAlign="center">
                  <Icon name="wrench" color="teal" />

                  <Header.Content>
                    Garantía local

                    <Header.Subheader>
                      El equipo continúa con el flujo normal de diagnóstico
                      y reparación en Servicio Técnico.
                    </Header.Subheader>
                  </Header.Content>
                </Header>
              </Segment>
            </Grid.Column>

            <Grid.Column>
              <Segment
                raised={tipoGarantia === "EXTERNA"}
                color={
                  tipoGarantia === "EXTERNA"
                    ? "orange"
                    : undefined
                }
                onClick={() => {
                  if (saving) return;
                  setTipoGarantia("EXTERNA");
                  /* setTipoGarantia("EXTERNA"); *///Desactivado externa.mientras no se desarrolle esta parte
                }}
                style={{
                  cursor: saving ? "default" : "pointer",
                  minHeight: 155,
                }}
              >
                <Header as="h4" textAlign="center">
                  <Icon name="truck" color="orange" />

                  <Header.Content>
                    Garantía proveedor

                    <Header.Subheader>
                      El ERP indica que un proveedor externo debe responder
                      por la garantía del equipo.
                    </Header.Subheader>
                  </Header.Content>
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>

          {tipoGarantia === "LOCAL" && (
            <Message positive>
              El equipo continuará al diagnóstico de FALLA, REPUESTO y
              SERVICIO.
            </Message>
          )}

          {tipoGarantia === "EXTERNA" && (
            <>
              <Message warning>
                El diagnóstico local no se iniciará. El equipo quedará
                derivado a gestión de garantía externa.
              </Message>

              <Form>
                <Form.Field required>
                  <label>
                    Proveedor identificado en ERP
                  </label>

                  <Dropdown
                    placeholder="Seleccionar proveedor externo"
                    fluid
                    search
                    selection
                    clearable
                    options={proveedoresOptions}
                    value={proveedorGarantia}
                    onChange={(e, { value }) =>
                      setProveedorGarantia(value || "")
                    }
                    disabled={saving}
                    noResultsMessage="No se encontraron proveedores ST externos"
                  />
                </Form.Field>
              </Form>
            </>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={() => {
              setOpenValidacionGarantia(false);
              setTipoGarantia("");
              setProveedorGarantia("");
            }}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            primary
            icon
            labelPosition="right"
            loading={saving}
            disabled={
              saving ||
              !tipoGarantia ||
              (tipoGarantia === "EXTERNA" &&
                !proveedorGarantia.trim())
            }
            onClick={confirmarValidacionGarantia}
          >
            Confirmar
            <Icon name="check" />
          </Button>
        </Modal.Actions>
      </Modal>

      {/* =======================================================
          MODAL CONFIRMAR DIAGNÓSTICO
      ======================================================= */}

      <Modal
        open={openSiguiente}
        size="tiny"
        onClose={() => setOpenSiguiente(false)}
      >
        <Modal.Header>
          Confirmar diagnóstico
        </Modal.Header>

        <Modal.Content>
          <p>
            ¿Confirmas el diagnóstico y deseas continuar a la ejecución?
          </p>

          <Message info>
            Una vez confirmado, se habilitará la etapa de ejecución de la
            garantía local.
          </Message>
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={() => setOpenSiguiente(false)}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            primary
            icon
            labelPosition="right"
            onClick={confirmarSiguiente}
            loading={saving}
            disabled={saving}
          >
            Confirmar
            <Icon name="arrow right" />
          </Button>
        </Modal.Actions>
      </Modal>

      {/* =======================================================
          MODAL ELIMINAR
      ======================================================= */}

      <Modal
        open={openDelete}
        size="tiny"
        onClose={() => setOpenDelete(false)}
      >
        <Modal.Header>
          Eliminar item
        </Modal.Header>

        <Modal.Content>
          ¿Estás seguro de eliminar este item?
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={() => setOpenDelete(false)}
          >
            Cancelar
          </Button>

          <Button
            negative
            icon
            labelPosition="right"
            onClick={doDelete}
          >
            Eliminar
            <Icon name="trash" />
          </Button>
        </Modal.Actions>
      </Modal>

      {/* =======================================================
          MODAL FINALIZAR
      ======================================================= */}

      <Modal
        open={openFinalizar}
        size="small"
        onClose={() => {
          if (!saving) {
            setOpenFinalizar(false);
          }
        }}
      >
        <Modal.Header>
          Finalizar Garantía
        </Modal.Header>

        <Modal.Content>
          <Message positive icon>
            <Icon name="check circle" />

            <Message.Content>
              <Message.Header>
                Resumen de la garantía
              </Message.Header>

              Revisa la información antes de finalizar.
            </Message.Content>
          </Message>

          <Table definition compact celled>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={8}>
                  Fallas
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.fallas}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell>
                  Repuestos
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.repuestos}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell>
                  Servicios
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.servicios}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell>
                  Instrumentos
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.instrumentos}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell>
                  Check
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.checks}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell>
                  Mediciones
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.mediciones}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell>
                  Seguridad
                </Table.Cell>
                <Table.Cell>
                  {resumenFinalizacion.seguridad}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Form>
            <Form.Field>
              <label>
                Observaciones finales
              </label>

              <Form.TextArea
                placeholder="Observaciones del cierre de la garantía..."
                value={observacionesFinales}
                onChange={(e, { value }) =>
                  setObservacionesFinales(value)
                }
              />
            </Form.Field>
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={() => setOpenFinalizar(false)}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            positive
            icon
            labelPosition="right"
            loading={saving}
            disabled={saving}
            onClick={confirmarFinalizar}
          >
            Finalizar
            <Icon name="check" />
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default EjecutarGarantia;
